import { Directive, ElementRef, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, Renderer2, AfterViewInit, SecurityContext } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

@Directive({
  selector: '[appExpandableText]',
  standalone: true
})
export class ExpandableTextDirective implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @Input() appExpandableText: string = ''; // The text content
  @Input() maxLines: number = 3; // Maximum lines before truncation
  @Input() allowHtml: boolean = false;
  @Input() showMoreText: string = 'COMMON.SHOW_MORE'; // Translation key for "Show more"
  @Input() showLessText: string = 'COMMON.SHOW_LESS'; // Translation key for "Show less"
  @Input() textColor: string = 'primary'; // Color for the toggle button
  @Input() backgroundColor: string = 'var(--bdt-background-color)'; // Background color for the toggle button

  private isExpanded = false;
  private originalText = '';
  private toggleButton: HTMLElement | null = null;
  private textContainer: HTMLElement | null = null;
  private translateSubscription: Subscription | undefined;
  private needsTruncation = false;
  private isViewInitialized = false;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private translate: TranslateService,
    private sanitizer: DomSanitizer,
  ) {}

  ngOnInit() {
    this.originalText = this.appExpandableText || '';
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['appExpandableText']) {
      this.originalText = this.appExpandableText || '';
      
      // Only rebuild if the view has been initialized
      if (this.isViewInitialized) {
        // Reset expanded state when content changes
        this.isExpanded = false;
        this.setupExpandableText();
      }
    }
  }

  ngAfterViewInit() {
    this.isViewInitialized = true;
    // Wait for view to be initialized before setting up expandable text
    setTimeout(() => {
      this.setupExpandableText();
    }, 100);
  }

  ngOnDestroy() {
    this.cleanup();
  }

  private cleanup() {
    if (this.translateSubscription) {
      this.translateSubscription.unsubscribe();
      this.translateSubscription = undefined;
    }
    this.toggleButton = null;
    this.textContainer = null;
    this.needsTruncation = false;
  }

  private setupExpandableText() {
    if (!this.originalText) {
      // Clear element if no text
      this.el.nativeElement.innerHTML = '';
      this.cleanup();
      return;
    }

    // Clean up existing elements
    this.cleanup();
    
    // Clear the element
    this.el.nativeElement.innerHTML = '';
    
    // Create text container with initial truncated state
    this.textContainer = this.renderer.createElement('div');
    this.renderer.addClass(this.textContainer, 'expandable-text-content');
    
    // Apply CSS-based line clamping initially
    this.renderer.setStyle(this.textContainer, 'display', '-webkit-box');
    this.renderer.setStyle(this.textContainer, '-webkit-box-orient', 'vertical');
    this.renderer.setStyle(this.textContainer, '-webkit-line-clamp', this.maxLines.toString());
    this.renderer.setStyle(this.textContainer, 'overflow', 'hidden');
    this.renderer.setStyle(this.textContainer, 'word-wrap', 'break-word');

    if (this.textContainer) {
      const renderedContent = this.allowHtml
        ? this.sanitizer.sanitize(SecurityContext.HTML, this.originalText) || ''
        : this.originalText;

      if (this.allowHtml) {
        this.textContainer.innerHTML = renderedContent;
      } else {
        this.textContainer.textContent = renderedContent;
      }
    }
    this.renderer.appendChild(this.el.nativeElement, this.textContainer);
    
    // Check if text needs truncation after rendering
    this.checkIfTruncationNeeded();
  }

  private checkIfTruncationNeeded() {
    if (!this.textContainer) return;
    if (this.maxLines <= 0) {
      this.needsTruncation = false;
      this.removeToggleButton();
      return;
    }
    
    // Wait for next frame to ensure styles are applied
    requestAnimationFrame(() => {
      if (!this.textContainer) return;

      const tolerancePx = 1;
      const clampedHeight = this.textContainer.getBoundingClientRect().height;

      this.renderer.setStyle(this.textContainer, 'display', 'block');
      this.renderer.removeStyle(this.textContainer, '-webkit-box-orient');
      this.renderer.removeStyle(this.textContainer, '-webkit-line-clamp');
      this.renderer.setStyle(this.textContainer, 'overflow', 'visible');

      const expandedHeight = this.textContainer.getBoundingClientRect().height;

      this.renderer.setStyle(this.textContainer, 'display', '-webkit-box');
      this.renderer.setStyle(this.textContainer, '-webkit-box-orient', 'vertical');
      this.renderer.setStyle(this.textContainer, '-webkit-line-clamp', this.maxLines.toString());
      this.renderer.setStyle(this.textContainer, 'overflow', 'hidden');

      this.needsTruncation = expandedHeight - clampedHeight > tolerancePx;

      if (this.needsTruncation && !this.toggleButton) {
        this.createToggleButton();
      } else if (!this.needsTruncation) {
        this.removeToggleButton();
      }
    });
  }

  private getLineHeight(element: HTMLElement): number {
    const style = window.getComputedStyle(element);
    const lineHeight = style.lineHeight;
    const fontSize = parseFloat(style.fontSize);
    
    if (lineHeight === 'normal') {
      // Default line-height is typically 1.2 times the font size
      return Math.ceil(fontSize * 1.2);
    } else if (lineHeight.includes('px')) {
      return Math.ceil(parseFloat(lineHeight));
    } else if (lineHeight.includes('em') || lineHeight.includes('rem')) {
      // For em/rem, multiply by font size
      return Math.ceil(fontSize * parseFloat(lineHeight));
    } else if (!isNaN(parseFloat(lineHeight))) {
      // If line-height is a pure number, multiply by font size
      return Math.ceil(fontSize * parseFloat(lineHeight));
    } else {
      // Fallback to default
      return Math.ceil(fontSize * 1.2);
    }
  }

  private createToggleButton() {
    // Create native button element (more predictable styling than ion-button here)
    this.toggleButton = this.renderer.createElement('button');
    this.renderer.setAttribute(this.toggleButton, 'type', 'button');

    this.renderer.addClass(this.toggleButton, 'expandable-text-button');
    this.renderer.setStyle(this.toggleButton, 'position', 'relative');
    this.renderer.setStyle(this.toggleButton, 'height', '3em');
    this.renderer.setStyle(this.toggleButton, 'bottom', '2em');
    this.renderer.setStyle(this.toggleButton, 'float', 'right');
    this.renderer.setStyle(this.toggleButton, 'display', 'inline-flex');
    this.renderer.setStyle(this.toggleButton, 'align-items', 'center');
    this.renderer.setStyle(this.toggleButton, 'color', 'var(--ion-color-primary)');
    this.renderer.setStyle(this.toggleButton, 'cursor', 'pointer');
    this.renderer.setStyle(this.toggleButton, 'border', '0');
    this.renderer.setStyle(this.toggleButton, 'outline', 'none');
    this.renderer.setStyle(this.toggleButton, 'font', 'inherit');
    this.renderer.setStyle(this.toggleButton, 'line-height', '1.2');
    this.renderer.setStyle(this.toggleButton, 'border-radius', 'var(--bdt-border-radius)');
    this.renderer.setStyle(this.toggleButton, 'padding-inline-start', '0.75rem');
    this.renderer.setStyle(this.toggleButton, 'padding-inline-end', '0.4rem');
    this.renderer.setStyle(this.toggleButton, 'padding-block', '0.15rem');
    this.renderer.setStyle(this.toggleButton, 'background', `radial-gradient(${this.backgroundColor} 0%, ${this.backgroundColor} 20%, transparent 100%)`);
    this.renderer.setStyle(this.toggleButton, 'font-weight', '500');

    // Set initial button text
    this.updateButtonText();
    
    // Prevent pointer/click events from bubbling to parent clickable containers
    this.renderer.listen(this.toggleButton, 'pointerdown', (event: Event) => {
      event.stopPropagation();
    });

    // Add click event listener
    this.renderer.listen(this.toggleButton, 'click', (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      this.toggle();
    });
    
    // Append button to the element
    this.renderer.appendChild(this.el.nativeElement, this.toggleButton);
  }

  private removeToggleButton() {
    if (!this.toggleButton) {
      return;
    }

    if (this.toggleButton.parentNode) {
      this.renderer.removeChild(this.el.nativeElement, this.toggleButton);
    }

    this.toggleButton = null;
  }

  private updateButtonText() {
    if (!this.toggleButton) return;
    
    const translationKey = this.isExpanded ? this.showLessText : this.showMoreText;
    
    // Subscribe to translation changes
    if (this.translateSubscription) {
      this.translateSubscription.unsubscribe();
    }
    
    this.translateSubscription = this.translate.get(translationKey).subscribe(translatedText => {
      if (this.toggleButton) {
        this.toggleButton.textContent = translatedText;
      }
    });
  }

  private updateDisplay() {
    if (!this.textContainer) return;
    
    if (this.isExpanded) {
      // Show full text - remove line clamp
      this.renderer.setStyle(this.textContainer, 'display', 'block');
      this.renderer.removeStyle(this.textContainer, '-webkit-box-orient');
      this.renderer.removeStyle(this.textContainer, '-webkit-line-clamp');
      this.renderer.setStyle(this.textContainer, 'overflow', 'visible');
      this.renderer.removeStyle(this.toggleButton, 'position');
      this.renderer.removeStyle(this.toggleButton, 'bottom');
      this.renderer.removeStyle(this.toggleButton, 'float');
    } else {
      // Apply line clamp truncation
      this.renderer.setStyle(this.textContainer, 'display', '-webkit-box');
      this.renderer.setStyle(this.textContainer, '-webkit-box-orient', 'vertical');
      this.renderer.setStyle(this.textContainer, '-webkit-line-clamp', this.maxLines.toString());
      this.renderer.setStyle(this.textContainer, 'overflow', 'hidden');
      this.renderer.setStyle(this.toggleButton, 'position', 'relative');
      this.renderer.setStyle(this.toggleButton, 'bottom', '2em');
      this.renderer.setStyle(this.toggleButton, 'float', 'right');
    }
    
    this.updateButtonText();
  }

  private toggle() {
    this.isExpanded = !this.isExpanded;
    this.updateDisplay();
  }
}
